/**
 * Gera um PDF simples (uma tabela, A4 deitado) no PROPRIO navegador.
 *
 * Feito a mao, sem biblioteca: a lista tem dado de paciente, entao nao passa
 * pelo servidor nem fica guardada em lugar nenhum — vira um arquivo baixado e
 * pronto. Usa a Helvetica que todo leitor de PDF ja tem (WinAnsi cobre os
 * acentos do portugues).
 *
 * gerarPdfTabela({ titulo, subtitulo, observacao, colunas: [{ titulo, largura }], linhas: [[...]] }) -> Blob
 * `largura` e a fracao da largura util (as colunas somam 1).
 */

const LARGURA = 842;
const ALTURA = 595;
const MARGEM = 36;
const LINHA = 16;
const CORPO = 9;

/** Texto -> bytes WinAnsi, com o escape de string do PDF. */
function texto(valor) {
  const s = String(valor ?? '')
    .normalize('NFC')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[^\x20-\xFF]/g, '?');
  return s.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

/** Corta para caber na coluna (largura media da Helvetica ~0,52 do corpo). */
function caber(valor, larguraPt, corpo) {
  const s = String(valor ?? '');
  const max = Math.max(1, Math.floor(larguraPt / (corpo * 0.52)));
  return s.length <= max ? s : `${s.slice(0, max - 3)}...`;
}

export function gerarPdfTabela({ titulo, subtitulo = '', observacao = '', colunas, linhas }) {
  const util = LARGURA - 2 * MARGEM;
  const xs = [];
  let x = MARGEM;
  for (const c of colunas) {
    xs.push(x);
    x += c.largura * util;
  }
  const larguras = colunas.map((c) => c.largura * util - 6);

  const topoTabela = ALTURA - MARGEM - 64;
  const porPagina = Math.max(1, Math.floor((topoTabela - MARGEM - 20) / LINHA));
  const paginas = [];
  for (let i = 0; i < Math.max(1, linhas.length); i += porPagina) paginas.push(linhas.slice(i, i + porPagina));

  const escrever = (fonte, corpo, px, py, t) => `BT /${fonte} ${corpo} Tf ${px.toFixed(1)} ${py.toFixed(1)} Td (${texto(t)}) Tj ET\n`;

  const conteudos = paginas.map((grupo, p) => {
    let c = '';
    c += escrever('F2', 14, MARGEM, ALTURA - MARGEM - 14, titulo);
    if (subtitulo) c += escrever('F1', 9, MARGEM, ALTURA - MARGEM - 30, subtitulo);
    if (observacao) c += escrever('F1', 8, MARGEM, ALTURA - MARGEM - 44, observacao);

    // Cabecalho da tabela.
    let y = topoTabela;
    c += `0.92 g ${MARGEM} ${y - 4} ${util} ${LINHA} re f 0 g\n`;
    colunas.forEach((col, i) => (c += escrever('F2', CORPO, xs[i] + 3, y + 1, caber(col.titulo, larguras[i], CORPO))));
    y -= LINHA;

    grupo.forEach((linha, n) => {
      if (n % 2 === 1) c += `0.97 g ${MARGEM} ${y - 4} ${util} ${LINHA} re f 0 g\n`;
      linha.forEach((valor, i) => (c += escrever('F1', CORPO, xs[i] + 3, y + 1, caber(valor, larguras[i], CORPO))));
      y -= LINHA;
    });
    c += `0.6 G 0.5 w ${MARGEM} ${y + LINHA - 4} m ${MARGEM + util} ${y + LINHA - 4} l S\n`;

    c += escrever('F1', 8, MARGEM, MARGEM - 12, `Página ${p + 1} de ${paginas.length}`);
    return c;
  });

  // Objetos: 1 catalogo, 2 paginas, 3 e 4 fontes; depois (pagina, conteudo) por pagina.
  const objetos = [];
  const idsPaginas = conteudos.map((_, i) => 5 + i * 2);
  objetos[1] = '<< /Type /Catalog /Pages 2 0 R >>';
  objetos[2] = `<< /Type /Pages /Kids [${idsPaginas.map((id) => `${id} 0 R`).join(' ')}] /Count ${idsPaginas.length} >>`;
  objetos[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';
  objetos[4] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>';
  conteudos.forEach((c, i) => {
    const idPagina = idsPaginas[i];
    objetos[idPagina] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${LARGURA} ${ALTURA}] ` +
      `/Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${idPagina + 1} 0 R >>`;
    objetos[idPagina + 1] = `<< /Length ${c.length} >>\nstream\n${c}endstream`;
  });

  // Tudo e WinAnsi (1 byte por caractere): o tamanho da string e o de bytes.
  let pdf = '%PDF-1.4\n';
  const offsets = [];
  for (let i = 1; i < objetos.length; i++) {
    offsets[i] = pdf.length;
    pdf += `${i} 0 obj\n${objetos[i]}\nendobj\n`;
  }
  const xref = pdf.length;
  pdf += `xref\n0 ${objetos.length}\n0000000000 65535 f \n`;
  for (let i = 1; i < objetos.length; i++) pdf += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objetos.length} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;

  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff;
  return new Blob([bytes], { type: 'application/pdf' });
}

/** Baixa o Blob com o nome dado. */
export function baixarArquivo(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
